import { createContext, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { API_BASE } from "config/api";
import { getAuthHeader } from "services/authHeader";

const SessionContext = createContext({ sessionExpired: false, dismissSessionExpired: () => {} });

// URL fragments that should NOT trigger the session-expired popup, even on 401.
// e.g., the login endpoint legitimately returns 401 for bad credentials.
const SESSION_EXEMPT_URL_FRAGMENTS = ["/api/auth/login"];

// Inactivity timeout: log the user out after 1h with no user interaction.
const IDLE_LIMIT_MS = 60 * 60 * 1000; // 1 hour
// While the user is active, slide the token forward at most this often.
const REFRESH_THROTTLE_MS = 5 * 60 * 1000; // 5 minutes
// User-input events that count as "activity" and keep the session alive.
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel",
];

function isAuthRequest(input) {
  let urlString = "";
  if (typeof input === "string") {
    urlString = input;
  } else if (input instanceof Request) {
    urlString = input.url;
  } else if (input instanceof URL) {
    urlString = input.toString();
  } else if (input && typeof input === "object" && "url" in input) {
    urlString = String(input.url);
  }
  return SESSION_EXEMPT_URL_FRAGMENTS.some((frag) => urlString.includes(frag));
}

export function SessionProvider({ children }) {
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || window.__fetchSessionWrapped) return;
    window.__fetchSessionWrapped = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const [input] = args;
      const response = await originalFetch(...args);
      try {
        if (response && response.status === 401 && !isAuthRequest(input)) {
          // Only show popup if user is currently logged-in (has any persisted auth state).
          const hasAuthState =
            localStorage.getItem("auth_token") || localStorage.getItem("user_id");
          if (hasAuthState) {
            setSessionExpired(true);
          }
        }
      } catch (e) {
        // never break the fetch chain because of our detection
      }
      return response;
    };
  }, []);

  // ---- Inactivity (idle) timeout + sliding token refresh ----
  const idleTimerRef = useRef(null);
  const lastRefreshRef = useRef(0);
  const sessionExpiredRef = useRef(sessionExpired);
  sessionExpiredRef.current = sessionExpired;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // Only arm the idle watcher when the user is logged in.
    if (!localStorage.getItem("auth_token")) return undefined;

    const armIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        // 1h with no activity → expire the session (same path a 401 takes).
        if (localStorage.getItem("auth_token") || localStorage.getItem("user_id")) {
          setSessionExpired(true);
        }
      }, IDLE_LIMIT_MS);
    };

    const slideTokenForward = async () => {
      const now = Date.now();
      if (now - lastRefreshRef.current < REFRESH_THROTTLE_MS) return;
      if (!localStorage.getItem("auth_token")) return;
      lastRefreshRef.current = now;
      try {
        const res = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: "POST",
          headers: getAuthHeader(),
        });
        if (res.ok) {
          const json = await res.json();
          const newToken = json && json.data && json.data.token;
          if (newToken) localStorage.setItem("auth_token", newToken);
        }
        // A 401 here is handled by the global fetch wrapper above (shows the dialog).
      } catch (e) {
        // network hiccup — allow a retry on the next activity tick
        lastRefreshRef.current = 0;
      }
    };

    const onActivity = () => {
      if (sessionExpiredRef.current) return; // stop sliding once expired
      armIdleTimer();
      slideTokenForward();
    };

    armIdleTimer();
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);

  const dismissSessionExpired = () => setSessionExpired(false);

  return (
    <SessionContext.Provider value={{ sessionExpired, dismissSessionExpired }}>
      {children}
    </SessionContext.Provider>
  );
}

SessionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useSession() {
  return useContext(SessionContext);
}
